<div class="modal-header">
  <h4 class="modal-title">{{ 'manageEvent.addPackage.title' | translate }}</h4>
  <button type="button" class="btn-close" aria-label="Close" (click)="close()"></button>
</div>

<div class="modal-body">
  <form [formGroup]="form">
    <!-- Package Name -->
    <div class="mb-5">
      <label class="form-label required">{{ 'manageEvent.addPackage.packageName' | translate }}</label>
      <input
        type="text"
        class="form-control"
        formControlName="package_name"
        [placeholder]="'manageEvent.addPackage.packageNamePlaceholder' | translate"
      />
      @if (form.get('package_name')?.touched && form.get('package_name')?.hasError('required')) {
        <div class="text-danger fs-7 mt-1">{{ 'manageEvent.addPackage.packageNameRequired' | translate }}</div>
      }
    </div>

    <!-- Package Description -->
    <div class="mb-5">
      <label class="form-label">{{ 'manageEvent.addPackage.description' | translate }}</label>
      <textarea
        class="form-control"
        formControlName="package_description"
        rows="3"
        [placeholder]="'manageEvent.addPackage.descriptionPlaceholder' | translate"
      ></textarea>
    </div>

    <div class="row mb-5">
      <!-- Session Count -->
      <div class="col-md-6">
        <label class="form-label required">{{ 'manageEvent.addPackage.sessionCount' | translate }}</label>
        <input
          type="number"
          class="form-control"
          formControlName="session_count"
          min="1"
        />
        @if (form.get('session_count')?.touched && form.get('session_count')?.hasError('required')) {
          <div class="text-danger fs-7 mt-1">{{ 'manageEvent.addPackage.sessionCountRequired' | translate }}</div>
        }
      </div>

      <!-- Session Type -->
      <div class="col-md-6">
        <label class="form-label required">{{ 'manageEvent.addPackage.sessionType' | translate }}</label>
        <select class="form-select" formControlName="session_type">
          @for (option of sessionTypeOptions; track option.value) {
            <option [value]="option.value">{{ option.label }}</option>
          }
        </select>
      </div>
    </div>

    <!-- Free Package Checkbox -->
    <div class="mb-5">
      <div class="form-check">
        <input
          class="form-check-input"
          type="checkbox"
          id="isFreePackage"
          [checked]="isFree"
          (change)="toggleFree($any($event.target).checked)"
        >
        <label class="form-check-label" for="isFreePackage">
          {{ 'manageEvent.addPackage.freePackage' | translate }}
        </label>
        <div class="form-text text-muted">{{ 'manageEvent.addPackage.freePackageHint' | translate }}</div>
      </div>
    </div>

    @if (!isFree) {
      <div class="row mb-5">
        <!-- Price -->
        <div class="col-md-4">
          <label class="form-label required">{{ 'manageEvent.addPackage.price' | translate }}</label>
          <input
            type="number"
            class="form-control"
            formControlName="price"
            min="0"
            step="0.01"
          />
          @if (v('price') > 0) {
            <div class="text-muted small mt-1">
              {{ 'common.netAmountHint' | translate:{ amount: calculateNetAmount(v('price')), currency: v('currency') || 'NOK' } }}
            </div>
          }
        </div>

        <!-- Currency -->
        <div class="col-md-4">
          <label class="form-label">{{ 'manageEvent.addPackage.currency' | translate }}</label>
          <select class="form-select" formControlName="currency" [attr.disabled]="true">
            @for (option of currencyOptions; track option.code) {
              <option [value]="option.code">{{ option.name }}</option>
            }
          </select>
        </div>

        <!-- VAT Rate -->
        <div class="col-md-4">
          <label class="form-label">{{ 'manageEvent.addPackage.vatRate' | translate }}</label>
          <select class="form-select" formControlName="vat_rate">
            @for (option of vatRateOptions; track option.value) {
              <option [value]="option.value">{{ option.label }}</option>
            }
          </select>
        </div>
      </div>
    }

    <!-- Day Distribution Section -->
    @if (getDaysWithCapacity().length > 0) {
      <div class="day-distribution-section mt-5">
        <div class="section-header mb-2">
          <i class="fa fa-calendar-alt text-primary me-2"></i>
          <span class="fw-semibold">{{ 'manageEvent.addPackage.sessionDistribution' | translate }}</span>
          <span class="text-danger ms-2">*</span>
        </div>
        <p class="text-muted small mb-3">
          {{ 'manageEvent.addPackage.distributionHelp' | translate }}
        </p>

        <div class="day-distribution-grid">
          @for (day of getDaysWithCapacity(); track day.training_date) {
            <div class="day-distribution-item">
              <label class="day-label">{{ day.dayLabel }}</label>
              <input
                type="number"
                class="form-control form-control-sm"
                min="0"
                [max]="v('session_count')"
                [(ngModel)]="day.session_count"
                [ngModelOptions]="{standalone: true}"
                placeholder="0"
              />
            </div>
          }
        </div>

        @if (getTotalDaySessions() > 0) {
          <div class="distribution-summary mt-2" [class.text-danger]="getTotalDaySessions() !== v('session_count')" [class.text-success]="getTotalDaySessions() === v('session_count')">
            <i class="fa" [class.fa-check-circle]="getTotalDaySessions() === v('session_count')" [class.fa-exclamation-circle]="getTotalDaySessions() !== v('session_count')"></i>
            {{ getTotalDaySessions() }} / {{ v('session_count') }} {{ 'manageEvent.addPackage.sessionsAllocated' | translate }}
            @if (getTotalDaySessions() !== v('session_count')) {
              <span class="ms-1">({{ 'manageEvent.addPackage.mustMatch' | translate }})</span>
            }
          </div>
        }
      </div>
    } @else if (packageDays.length > 0) {
      <div class="alert alert-info small mb-0 mt-5">
        <i class="fa fa-info-circle me-2"></i>
        {{ 'manageEvent.addPackage.setupCapacityFirst' | translate }}
      </div>
    }

  </form>
</div>

<div class="modal-footer">
  <button type="button" class="btn btn-light" (click)="close()">{{ 'common.cancel' | translate }}</button>
  <button type="button" class="btn btn-primary" (click)="save()" [disabled]="saving">
    @if (saving) {
      <span class="spinner-border spinner-border-sm me-2"></span>
    }
    {{ 'manageEvent.addPackage.createPackage' | translate }}
  </button>
</div>
